/* LS TV — pinned scroll animation.
 *
 * Opening frame: one hero video card filling the entire screen (scaled to
 * cover, so the surplus of its 16:9-ish aspect is cropped). Scrolling scrubs
 * it down to its slot — the middle one — in a three-card row (order 2-1-3)
 * while cards 2 and 3 fade in from a slight off-axis offset (left card from
 * x:-50, right from x:+50) and slide
 * into their grid positions. The row is the real layout the whole time — only
 * transforms move, nothing reflows — so the pin geometry is measured once.
 *
 * Once the card has landed, a last scrubbed phase of the same pin expands the
 * collapsed .ls-tv__below block: the marquee of further thumbnails and the
 * "Plus de vidéos" button travel up into the frame from under the bottom edge
 * of the screen and push the row up. It is scroll-driven rather than a CSS
 * transition fired on landing: a timed transition kept running while the user
 * scrubbed back into the pin (the row sawtoothed between the two motions) and,
 * on the way out, Chrome's scroll anchoring fought it by nudging the page
 * backwards. .ls-tv--settled still marks the landed state for the heading.
 *
 * Differences from the FCSG effect this was ported from:
 * - kills only its own ScrollTrigger (FCSG killed every trigger on the page);
 * - reveal is one class toggle + CSS transitions (FCSG tweened per frame);
 * - geometry is computed (viewport/card/header) instead of per-breakpoint
 *   magic numbers, so the same code runs on mobile;
 * - the marquee is a hand-rolled rAF ticker (FCSG shipped Flickity for it).
 *
 * Load order: deferred after gsap/ScrollTrigger in the section; jQuery is
 * theme-wide. All entry points run at DOM-ready, when everything is in.
 */

$(document).ready(function () {
  var wrapper = document.querySelector('.ls-tv__wrapper');
  if (!wrapper) return;
  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
    /* No GSAP/ScrollTrigger, no pin — drop the curtain offset so the section
       stays in flow, and settle it directly: without .ls-tv--settled the head,
       marquee and button would keep their pre-reveal state (invisible /
       translated off-screen), and without .ls-tv--no-ticker the strip would
       sit frozen instead of degrading to a native scroll row. */
    wrapper.classList.remove('ls-tv__wrapper--curtain');
    var staticSection = wrapper.querySelector('.ls-tv__section');
    if (staticSection) staticSection.classList.add('ls-tv--settled', 'ls-tv--no-ticker');
    return;
  }

  var section = wrapper.querySelector('.ls-tv__section');

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  /* Touch devices get the settled layout: the pin turns a flick into a long
     scrubbed stretch there. Must mirror the curtain media gate in ls-tv.css. */
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var animationEnabled = wrapper.dataset.enableAnimation === 'true' && !reducedMotion && finePointer;

  gsap.registerPlugin(ScrollTrigger);
  gsap.config({ nullTargetWarn: false });

  /* ---------- pinned scrub ---------- */

  var timeline = null;
  /* The `top <offset>` the live trigger was created with (see checkDrift). */
  var pinOffset = 0;

  /* The row children are the three cards (or, until the aico feed lands, the
     three skeletons that stand in for them at the same size). */
  function rowItems() {
    var row = wrapper.querySelector('.ls-tv__row');
    return row ? Array.prototype.slice.call(row.children) : [];
  }

  function killAnimation() {
    if (timeline) {
      if (timeline.scrollTrigger) timeline.scrollTrigger.kill();
      timeline.kill();
      timeline = null;
    }
    gsap.set(wrapper.querySelectorAll('.ls-tv__row > *, .ls-tv__img-wrap, .ls-tv__play, .ls-tv__card-content'), {
      clearProps: 'transform,opacity,visibility,left,right,bottom',
    });
    gsap.set(wrapper.querySelectorAll('.ls-tv__below'), { clearProps: 'height,marginTop' });
    gsap.set(wrapper.querySelectorAll('.ls-tv__below > *'), { clearProps: 'transform' });
  }

  /* The below-stage block (marquee + button) is collapsed to height 0 until the
     row lands, so the hero is centred instead of being pushed up by two
     invisible flex items. Every layout read has to happen against that
     collapsed frame — it is the one the pin geometry belongs to, and an
     expanded block reports back the height it was given rather than the height
     its content wants. Since init re-runs on resize and after the feed lands,
     both possible while the section is settled, the state is forced. */
  function collapseBelow() {
    section.classList.add('ls-tv--measuring');
  }

  function restoreBelow() {
    var below = section.querySelector('.ls-tv__below');
    /* The restored height must be flushed while the transition is still off:
       collapse and restore are one task and never paint, but the forced layout
       in between is a style recalc the transition would otherwise start from,
       replaying the push-up on every measurement. */
    section.classList.add('ls-tv--measured');
    section.classList.remove('ls-tv--measuring');
    if (below) void below.offsetHeight;
    section.classList.remove('ls-tv--measured');
  }

  /* `height` has to transition to a length, so the content height is handed to
     CSS from here (scrollHeight reports it even at height 0). */
  function measureBelow() {
    var below = section.querySelector('.ls-tv__below');
    if (!below) return 0;
    collapseBelow();
    var height = below.scrollHeight;
    wrapper.style.setProperty('--ls-tv-below-h', height + 'px');
    restoreBelow();
    return height;
  }

  /* How much of the viewport top the header bar actually covers.
     The header *section* is a zero-height wrapper around a position:fixed bar,
     so its own rect (headerOffset below) says nothing about what is hidden
     behind it. Only the part the pin does not already account for counts, so a
     header that sits in flow is never charged twice. */
  function coveredTop(headerOffset) {
    var bar = document.querySelector('.section-header .header-wrapper, .shopify-section-header .header-wrapper');
    if (!bar) return 0;
    var position = window.getComputedStyle(bar).position;
    if (position !== 'fixed' && position !== 'sticky') return 0;
    return Math.max(0, Math.round(bar.getBoundingClientRect().height) - headerOffset);
  }

  function initializeAnimation() {
    killAnimation();

    var items = rowItems();
    if (!items.length) return;
    /* The row is laid out 2-1-3 (snippets/ls-tv-js.liquid), so the hero is the
       middle card — the slot the shrink lands in is already horizontally
       centred. Before the feed arrives the middle skeleton stands in for it.
       sides keeps DOM order, so sides[0] is the left card and sides[1] the
       right one, which is what the fade directions below assume. */
    var heroIndex = items.length > 1 ? 1 : 0;
    var hero = items[heroIndex];
    var sides = items.filter(function (item, index) {
      return index !== heroIndex;
    });

    var belowH = measureBelow();

    /* Every rect read below describes the un-settled layout — see collapseBelow. */
    collapseBelow();

    var header = document.querySelector('.shopify-section-header, .section-header');
    var headerOffset = header ? Math.round(header.getBoundingClientRect().height) : 0;
    var covered = coveredTop(headerOffset);
    /* Section height while pinned. The settled row is centred in the slice the
       fixed bar leaves open (--ls-tv-covered, via the section's top padding in
       CSS); the fullscreen opening frame deliberately ignores that and fills
       the whole thing. */
    var visibleH = window.innerHeight - headerOffset;
    pinOffset = headerOffset;

    /* Feed the curtain CSS (wrapper margin-top, section min-height + top
       padding, stage width). Set before any geometry is read below so the
       reads see the settled layout — the getBoundingClientRect() calls flush
       it. */
    wrapper.style.setProperty('--ls-tv-header-offset', headerOffset + 'px');
    wrapper.style.setProperty('--ls-tv-covered', covered + 'px');

    var box = hero.getBoundingClientRect();
    var sectionBox = section.getBoundingClientRect();

    /* How far the below-stage block has to travel to start off the bottom edge
       of the screen — read here, against the collapsed frame, because that is
       the layout the block leaves from (see the reveal rules in ls-tv.css).
       The floor covers short/odd viewports where the collapsed block already
       sits at or past the section's bottom. */
    var belowEl = section.querySelector('.ls-tv__below');
    var belowTop = belowEl ? belowEl.getBoundingClientRect().top : sectionBox.bottom;
    var rise = Math.max(200, Math.round(sectionBox.bottom - belowTop) + 24);
    wrapper.style.setProperty('--ls-tv-rise', rise + 'px');
    var belowGap = belowEl ? parseFloat(window.getComputedStyle(belowEl).rowGap) || 0 : 0;

    restoreBelow();

    if (!box.width || !box.height) return;

    var narrow = window.innerWidth < 750;

    /* Opening frame: the entire screen. The card is scaled until it covers
       both axes of the pinned viewport — its 1104/612 aspect rarely matches
       the screen's, so the surplus on the wider axis is cropped by the card's
       own overflow:hidden, exactly like a background-size:cover image. */
    var scaleStart = Math.max(1, window.innerWidth / box.width, visibleH / box.height);

    /* Where the hero's centre has to sit: the middle of the section, which
       while pinned is the middle of the screen (the fixed bar overlays the top
       of the fullscreen video rather than pushing it down — that is what makes
       the frame full-bleed). Horizontally it moves from its grid slot's centre
       to the viewport centre — with the hero in the middle slot that is a
       near-zero shift, but it is still measured rather than assumed (the stage
       can sit off-centre, e.g. under a scrollbar). */
    var cardCenterInSection = box.top - sectionBox.top + box.height / 2;
    var yOffset = visibleH / 2 - cardCenterInSection;
    var xOffset = window.innerWidth / 2 - (box.left + box.width / 2);

    var imgWrap = hero.querySelector('.ls-tv__img-wrap');
    var play = hero.querySelector('.ls-tv__play');
    var content = hero.querySelector('.ls-tv__card-content');

    /* The title and the gradient are meant to grow with the hero, but the play
       circle is not — at full card scale it would be ~150px across. It is
       counter-scaled to a fixed hero size and eases back to its CSS size as
       the card shrinks. */
    var playBase = play ? play.offsetWidth : 0;
    var playScale = playBase ? Math.min(1, (narrow ? 72 : 96) / (playBase * scaleStart)) : 1;

    /* How far off-axis cards 2 and 3 start (the user's spec: ~50px). */
    var sideShift = narrow ? 32 : 50;

    var settled = section.classList.contains('ls-tv--settled');

    /* The caption block is inset by exactly what the cover crop cuts off AT
       THE CURRENT SCALE: while the card is bigger than the screen the title
       and the gradient sit on the visible edge instead of being cut off with
       the rest of the overflow, and the moment the card is small enough not to
       be cropped they are back on its own edges. Driven off the live scale
       rather than tweened alongside it — a tween of its own lagged the shrink
       and left the title floating above the card's bottom. */
    function syncCaption(scale) {
      if (!content || !scale) return;
      var insetX = Math.max(0, (box.width * scale - window.innerWidth) / 2) / scale;
      var insetY = Math.max(0, (box.height * scale - visibleH) / 2) / scale;
      gsap.set(content, { left: insetX, right: insetX, bottom: insetY });
    }

    /* Scroll budget in px, so the phases stay in sync with the curtain:
       the pin engages when the section's top meets the header, i.e. when the
       curtain's bottom edge is at the viewport bottom — the hero frame must
       therefore be held for at least the remaining curtain travel (visibleH)
       before anything shrinks. Then a short beat, the shrink, and the rise of
       the below-stage block.
       With scrub, timeline durations are in the same px units as `end`. */
    var revealDist = visibleH;
    var holdDist = Math.round(window.innerHeight * 0.15);
    var shrinkDist = Math.round(window.innerHeight * 0.75);
    var riseDist = belowEl && belowH ? Math.round(window.innerHeight * 0.3) : 0;
    var holdPhase = revealDist + holdDist;

    timeline = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: 'top ' + headerOffset + 'px',
        end: '+=' + (revealDist + holdDist + shrinkDist + riseDist),
        scrub: true,
        pin: true,
        anticipatePin: 1,
        onUpdate: function () {
          var scaleNow = gsap.getProperty(hero, 'scaleX');
          syncCaption(scaleNow);
          var isSettled = scaleNow <= 1.02;
          if (isSettled !== settled) {
            settled = isSettled;
            section.classList.toggle('ls-tv--settled', isSettled);
          }
        },
      },
    });

    timeline
      .addLabel('hold', 0)
      .fromTo(
        hero,
        { scale: scaleStart, x: xOffset, y: yOffset },
        { scale: scaleStart, x: xOffset, y: yOffset, duration: holdPhase, ease: 'none' },
        'hold'
      )
      .addLabel('shrink', holdPhase)
      /* Near-linear: with scrub the scroll wheel is the timing curve, and a
         power2.out here dumped almost all of the shrink into the first
         quarter, so the side cards had nothing left to arrive alongside. */
      .to(hero, { scale: 1, x: 0, y: 0, duration: shrinkDist, ease: 'power1.inOut' }, 'shrink');

    /* autoAlpha, not opacity: the side cards are links, and an invisible link
       over the hero would still swallow clicks. */
    sides.forEach(function (side, index) {
      var from = index === 0 ? -sideShift : sideShift;
      timeline.fromTo(
        side,
        { autoAlpha: 0, x: from },
        { autoAlpha: 0, x: from, duration: holdPhase, ease: 'none' },
        'hold'
      );
    });
    if (sides.length) {
      /* They start arriving once the hero is visibly on its way down, and
         finish with it — the row assembles around the shrinking card rather
         than after it. */
      timeline.to(
        sides,
        { autoAlpha: 1, x: 0, duration: Math.round(shrinkDist * 0.72), ease: 'power2.out' },
        'shrink+=' + Math.round(shrinkDist * 0.28)
      );
    }

    if (imgWrap) {
      timeline.fromTo(imgWrap, { scale: 1.15 }, { scale: 1, duration: holdPhase, ease: 'power1.out' }, 'hold');
    }
    if (play) {
      timeline
        .fromTo(play, { scale: playScale }, { scale: playScale, duration: holdPhase, ease: 'none' }, 'hold')
        .to(play, { scale: 1, duration: shrinkDist, ease: 'power2.out' }, 'shrink');
    }
    /* The below-stage block grows from its collapsed height (the flex gap
       cancelled by a negative margin, as in ls-tv.css) to its content height,
       pushing the centred row up, while the strip and the button travel up
       from under the bottom edge. The section's min-height already fits the
       grown block, so the pin spacer measured at progress 0 stays right. */
    if (riseDist) {
      var belowItems = belowEl.querySelectorAll('.ls-tv__marquee, .ls-tv__button');
      timeline
        .addLabel('rise', holdPhase + shrinkDist)
        .fromTo(
          belowEl,
          { height: 0, marginTop: -belowGap },
          { height: belowH, marginTop: 0, duration: riseDist, ease: 'none' },
          'rise'
        )
        .fromTo(belowItems, { y: rise }, { y: 0, duration: riseDist, ease: 'none' }, 'rise');
    }

    /* The trigger only reports scale once it starts scrubbing — seed the
       caption from the frame the timeline has just put the card in. */
    syncCaption(gsap.getProperty(hero, 'scaleX') || scaleStart);

    /* Timeline is live: the card now carries its hero transform, so the stage
       can be revealed (see the FOUC guard in ls-tv.css). */
    wrapper.classList.add('ls-tv--ready');
  }

  /* ---------- marquee ---------- */

  var marqueeEl = wrapper.querySelector('.ls-tv__marquee');
  var trackEl = wrapper.querySelector('.ls-tv__marquee-track');
  var marqueeState = { x: 0, base: 0, raf: null, hovering: false, visible: false, started: false };

  function initMarquee() {
    if (!trackEl || !trackEl.children.length) return;

    if (!animationEnabled) {
      /* Native scroll strip instead of an auto-ticker. */
      section.classList.add('ls-tv--no-ticker');
      return;
    }
    if (marqueeState.started) return;

    /* Measure BEFORE claiming `started`: inside an unplaced theme slot source
       (display:none until content-builder-neo-ext.js moves it) the strip
       measures 0, which would duplicate ten empty copies and freeze base at 0
       for good. Bail untouched instead — the next initAll(), fired by
       `ls:theme-slots-placed` below, measures the strip in its final home. */
    var strip = trackEl.scrollWidth;
    if (!strip) return;

    marqueeState.started = true;

    /* Duplicate the strip until it can wrap seamlessly. The ticker resets after
       translating one strip's width, so the track has to carry a full strip
       *plus* a viewport — measuring against 2x the viewport instead let a long
       strip through unduplicated and exposed bare gold before every wrap. */
    var original = trackEl.innerHTML;
    var copies = 1;
    while (trackEl.scrollWidth < strip + window.innerWidth && copies < 10) {
      trackEl.innerHTML += original;
      copies += 1;
    }
    /* base = width of one original strip incl. the joining gap */
    marqueeState.base = trackEl.scrollWidth / copies;

    trackEl.addEventListener('mouseenter', function () {
      marqueeState.hovering = true;
    });
    trackEl.addEventListener('mouseleave', function () {
      marqueeState.hovering = false;
    });

    new IntersectionObserver(function (entries) {
      marqueeState.visible = entries[0].isIntersecting;
      if (marqueeState.visible && marqueeState.raf === null) {
        marqueeState.raf = requestAnimationFrame(tick);
      }
    }).observe(marqueeEl);

    marqueeState.raf = requestAnimationFrame(tick);
  }

  function tick() {
    if (!marqueeState.visible) {
      marqueeState.raf = null;
      return;
    }
    marqueeState.x -= marqueeState.hovering ? 0.15 : 0.6;
    if (-marqueeState.x >= marqueeState.base) {
      marqueeState.x += marqueeState.base;
    }
    trackEl.style.transform = 'translate3d(' + marqueeState.x + 'px,0,0)';
    marqueeState.raf = requestAnimationFrame(tick);
  }

  /* ---------- wiring ---------- */

  function initAll() {
    initMarquee();
    if (animationEnabled) {
      requestAnimationFrame(function () {
        initializeAnimation();
        ScrollTrigger.refresh();
      });
    } else {
      /* Animation off or reduced motion: no curtain (the CSS media gate says
         the same thing — this is the belt-and-braces half). */
      wrapper.classList.remove('ls-tv__wrapper--curtain');
      section.classList.add('ls-tv--settled');
    }
  }

  subscribe('feed-loaded', function (e) {
    if (!e || e.area !== 'lsTv') return;
    initAll();
    /* The marquee strip only exists from here on (and initMarquee has just
       duplicated it) — re-measure what the settled block expands to. */
    measureBelow();
  });

  /* The feed may already be in the DOM (sessionStorage cache published before
     our subscription landed) — or never arrive (API down): init what exists. */
  initAll();

  $(window).on('load', function () {
    if (animationEnabled) {
      initializeAnimation();
      ScrollTrigger.refresh();
    }
  });

  /* The trigger's start/end are document offsets frozen at the last refresh,
     but the sections above keep settling after window load: the aico
     next-match band and the saison header render from API responses (the band
     grew 0 -> 446px after the final refresh in one load) and next-match
     re-renders on its live polls. A stale start engages the pin with the
     section still that far down the screen — it snaps to the top — and
     releases it the same distance off at the end. The pin spacer stays in
     flow at the section's real position, so any resize of the document is
     checked against it and only an actual drift pays for a refresh. */
  var driftTimer = null;
  function checkDrift() {
    var trigger = timeline && timeline.scrollTrigger;
    if (!trigger) return;
    var spacer = trigger.spacer || section.parentElement;
    var top = spacer.getBoundingClientRect().top + window.pageYOffset;
    if (Math.abs(top - pinOffset - trigger.start) > 1) ScrollTrigger.refresh();
  }
  if (animationEnabled && typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(function () {
      clearTimeout(driftTimer);
      driftTimer = setTimeout(checkDrift, 150);
    }).observe(document.body);
  }

  var resizeTimer = null;
  $(window).on('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      if (animationEnabled) {
        initializeAnimation();
        ScrollTrigger.refresh();
      }
    }, 250);
  });

  /* Neo theme slots: on the >64KB URL-fallback path (and editor re-renders)
     the section is moved into the aico document AFTER this ran, so every rect
     the pin took is stale and the marquee may have measured 0 while hidden.
     Re-init from scratch — initializeAnimation kills the old ScrollTrigger. */
  document.addEventListener('ls:theme-slots-placed', function (e) {
    var placed = e && e.detail && e.detail.placed;
    if (placed && placed.indexOf('ls-tv') === -1) return;
    initAll();
    measureBelow();
  });

  /* Theme editor: rebuild on section reload, clean up on removal. */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector && e.target.querySelector('.ls-tv__section')) {
      wrapper = e.target.querySelector('.ls-tv__wrapper') || wrapper;
      section = wrapper.querySelector('.ls-tv__section');
      marqueeEl = wrapper.querySelector('.ls-tv__marquee');
      trackEl = wrapper.querySelector('.ls-tv__marquee-track');
      marqueeState.started = false;
      initAll();
    }
  });
  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target.querySelector && e.target.querySelector('.ls-tv__section')) {
      killAnimation();
    }
  });
});
